import { useCallback, useEffect, useRef } from "react";

import { setBadgeCountAsync } from "@/crossPlatform/expo-notifications";
import { useSendbirdChat } from "@/crossPlatform/Sendbird";
import useAppMonitor from "@/services/AppMonitor";
import { useNotificationsDispatch } from "@/store/reducers/notifications";

const BadgeCountOptions = {
  web: { favicon: {}, method: "Favicon" as const, title: {} },
};

/**
 * Manages unread message count from Sendbird
 *
 * Updates both:
 * - App icon/tab badge (native: app icon badge; web: favicon badge via expo-notifications)
 * - Redux state for UI components to display counts
 *
 * @param isConnected - Whether the Sendbird connection is active
 * @returns Object with getUnreadMessageCountAsync and resetUnreadMessageCountAsync functions
 */
const useUnreadMessageCount = (isConnected: boolean) => {
  const { setMessagesBadgeCount } = useNotificationsDispatch();
  const { captureException } = useAppMonitor();
  const { sdk: sendbirdSdk } = useSendbirdChat();

  const setMessagesBadgeCountRef = useRef(setMessagesBadgeCount);
  useEffect(() => {
    setMessagesBadgeCountRef.current = setMessagesBadgeCount;
  }, [setMessagesBadgeCount]);

  const getUnreadMessageCountAsync = useCallback(async () => {
    if (!isConnected) return;

    try {
      const sendbirdCount =
        await sendbirdSdk.groupChannel.getTotalUnreadMessageCount();

      await setBadgeCountAsync(sendbirdCount, BadgeCountOptions);
      setMessagesBadgeCountRef.current(sendbirdCount);
    } catch (err) {
      captureException(err);
    }
  }, [isConnected, sendbirdSdk, captureException]);

  const resetUnreadMessageCountAsync = useCallback(async () => {
    try {
      await setBadgeCountAsync(0, BadgeCountOptions);
      setMessagesBadgeCountRef.current(0);
    } catch (err) {
      captureException(err);
    }
  }, [captureException]);

  return {
    getUnreadMessageCountAsync,
    resetUnreadMessageCountAsync,
  };
};

export default useUnreadMessageCount;
